// OTA-safe cold-launch boot recovery for the installed iOS WebView and web.
//
// Goals:
//  1. If the React app fails to mount within a window, attempt ONE safe
//     cache-busting reload. Never loop.
//  2. If the deployed web build version (BUILD_ID) differs from the version
//     last seen by this WebView, allow ONE cache-busting reload to break a
//     stale shell.
//  3. Expose helpers used by the Roava recovery UI ("Refresh app").
//
// No native APIs, no Supabase, no auth dependencies.

import { BUILD_ID } from "./build-info";

const SS_BOOT_ATTEMPTED = "roava_boot_recovery_attempted";
const LS_WEB_BUILD = "roava_web_build_version";
const RELOAD_QS_KEY = "roava_reload";

export const ROAVA_WEB_BUILD_VERSION = BUILD_ID;

function safeSession(): Storage | null {
  try { return window.sessionStorage; } catch { return null; }
}
function safeLocal(): Storage | null {
  try { return window.localStorage; } catch { return null; }
}

/** Mark the start of a boot attempt. Only the FIRST attempt within a session
 *  is allowed to trigger a recovery reload. */
export function markBootAttempt(): void {
  const ss = safeSession();
  if (!ss) return;
  if (!ss.getItem(SS_BOOT_ATTEMPTED)) {
    ss.setItem(SS_BOOT_ATTEMPTED, "0");
  }
}

/** Called once the React tree has rendered. Clears guard so the NEXT cold
 *  launch starts fresh, and stores the current web build version. */
export function markBootSuccess(): void {
  const ss = safeSession();
  const ls = safeLocal();
  try { ss?.removeItem(SS_BOOT_ATTEMPTED); } catch { /* noop */ }
  try { ls?.setItem(LS_WEB_BUILD, ROAVA_WEB_BUILD_VERSION); } catch { /* noop */ }
  // Strip the cache-busting marker from the URL so it doesn't stick around.
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.has(RELOAD_QS_KEY)) {
      url.searchParams.delete(RELOAD_QS_KEY);
      window.history.replaceState(null, "", url.toString());
    }
  } catch {
    /* noop */
  }
}

export function clearBootRecoveryGuard(): void {
  try { safeSession()?.removeItem(SS_BOOT_ATTEMPTED); } catch { /* noop */ }
}

function canAttemptReload(): boolean {
  const ss = safeSession();
  if (!ss) return false;
  const v = ss.getItem(SS_BOOT_ATTEMPTED);
  // "0" = boot recorded but no reload yet → allowed.
  // "1" = already reloaded this session → not allowed (prevents loop).
  return v === null || v === "0";
}

function recordReloadConsumed(): void {
  try { safeSession()?.setItem(SS_BOOT_ATTEMPTED, "1"); } catch { /* noop */ }
}

/** Hard reload with a cache-busting query param. At most once per session. */
export function requestCacheBustingReload(): void {
  if (!canAttemptReload()) {
    // Fall back to plain reload — guard already burned this session.
    try { window.location.reload(); } catch { /* noop */ }
    return;
  }
  recordReloadConsumed();
  try {
    const url = new URL(window.location.href);
    url.searchParams.set(RELOAD_QS_KEY, String(Date.now()));
    window.location.replace(url.toString());
  } catch {
    try { window.location.reload(); } catch { /* noop */ }
  }
}

/** If a newer web build is installed than this WebView has seen, allow ONE
 *  cache-busting reload to break a stale HTML shell after an OTA publish.
 *  Called very early at boot. */
export function checkVersionAndMaybeReload(): void {
  if (ROAVA_WEB_BUILD_VERSION === "dev") return;
  const ls = safeLocal();
  if (!ls) return;
  let stored: string | null = null;
  try { stored = ls.getItem(LS_WEB_BUILD); } catch { return; }
  if (!stored) {
    // First run on this device — just record and continue.
    try { ls.setItem(LS_WEB_BUILD, ROAVA_WEB_BUILD_VERSION); } catch { /* noop */ }
    return;
  }
  if (stored !== ROAVA_WEB_BUILD_VERSION && canAttemptReload()) {
    try { ls.setItem(LS_WEB_BUILD, ROAVA_WEB_BUILD_VERSION); } catch { /* noop */ }
    requestCacheBustingReload();
  }
}

/** Watchdog: if React has not mounted into #root within `timeoutMs`, attempt
 *  ONE safe reload. Bails immediately if the guard is burned. */
export function installBootWatchdog(timeoutMs = 8000): void {
  if (!canAttemptReload()) return;
  const start = Date.now();
  window.setTimeout(() => {
    try {
      const root = document.getElementById("root");
      const mounted = !!(root && root.childElementCount > 0);
      if (mounted) return;
      if (Date.now() - start < timeoutMs) return;
      // Still blank → attempt a one-shot cache-busting reload.
      requestCacheBustingReload();
    } catch {
      /* noop */
    }
  }, timeoutMs);
}
